import {
  Box,
  CircularProgress,
  Container,
  Paper,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import {
  getAllCategoriesWithGroups,
  getAllCategoryStats,
  getDateRangeFromMonths,
  getExpensesByCategory,
  getExpensesByVendor,
  getIncomeByCategory,
  getInvestmentsBySource,
  getTopExpenses,
  getTotals,
  getUpcomingRecurringPayments,
} from "../../services/statsService";
import { AuthContext } from "../Auth/AuthContext";
import { useGroupContext } from "../Groups/useGroupContext";
import CategoryChartsPage from "./CategoryChartsPage";
import CategoryFilter from "./CategoryFilter";
import CategoryStatsChart from "./CategoryStatsChart";
import EnhancedDatePicker from "./EnhancedDatePicker";
import RadialChart from "./RadialChart";
import TopExpensesChart from "./TopExpensesChart";
import TotalStats from "./TotalStats";
import UpcomingPaymentsChart from "./UpcomingPaymentsChart";
import useCategoryFilter from "./UseCategoryFilter";

const StatsPage = () => {
  const [months, setMonths] = useState(
    () => Number(localStorage.getItem("selectedMonths")) || 6,
  );
  const [dateMode, setDateMode] = useState(
    () => localStorage.getItem("dateMode") || "preset",
  );
  const [customDateRange, setCustomDateRange] = useState(() => {
    const saved = localStorage.getItem("customDateRange");
    return saved
      ? JSON.parse(saved)
      : {
          startDate: "",
          endDate: "",
        };
  });
  const [range, setRange] = useState(() => {
    const saved = localStorage.getItem("range");
    return saved ? JSON.parse(saved) : getDateRangeFromMonths("6");
  });
  const [activeTab, setActiveTab] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [upcomingPaymentsPeriod, setUpcomingPaymentsPeriod] = useState(1);

  const [totalStats, setTotalStats] = useState(null);
  const [categoryStats, setCategoryStats] = useState(null);
  const [expensesByCategory, setExpensesByCategory] = useState(null);
  const [investmentsBySource, setInvestmentsBySource] = useState(null);
  const [expensesByVendor, setExpensesByVendor] = useState(null);
  const [incomeByCategory, setIncomeByCategory] = useState(null);

  const [upcomingPayments, setUpcomingPayments] = useState(null);
  const [topExpenses, setTopExpenses] = useState(null);

  // Category filter state
  const [allCategories, setAllCategories] = useState([]);
  const { filteredCategoryIds, setFilteredCategoryIds } =
    useCategoryFilter(allCategories);

  const { getContextParams, isPersonalMode } = useGroupContext();
  const { userCurrency } = useContext(AuthContext);

  const getTodayString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const updateDateRangeToToday = (
    currentDateMode,
    currentMonths,
    currentCustomRange,
  ) => {
    const today = getTodayString();

    if (currentDateMode === "preset") {
      const updatedRange = getDateRangeFromMonths(currentMonths);
      setRange(updatedRange);
      localStorage.setItem("range", JSON.stringify(updatedRange));
      return updatedRange;
    } else {
      if (currentCustomRange.endDate && currentCustomRange.endDate !== today) {
        const updatedCustomRange = {
          ...currentCustomRange,
          endDate: today,
        };
        setCustomDateRange(updatedCustomRange);
        setRange(updatedCustomRange);
        localStorage.setItem(
          "customDateRange",
          JSON.stringify(updatedCustomRange),
        );
        localStorage.setItem("range", JSON.stringify(updatedCustomRange));
        return updatedCustomRange;
      }
      return currentCustomRange;
    }
  };

  useEffect(() => {
    const today = getTodayString();
    const updatedRange = updateDateRangeToToday(
      dateMode,
      months,
      customDateRange,
    );
    if (JSON.stringify(updatedRange) !== JSON.stringify(range)) {
      setRange(updatedRange);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("selectedMonths", months.toString());
    localStorage.setItem("dateMode", dateMode);

    if (dateMode === "preset") {
      const newRange = getDateRangeFromMonths(months.toString());
      setRange(newRange);
      localStorage.setItem("range", JSON.stringify(newRange));
    } else if (dateMode === "custom") {
      if (customDateRange.startDate && customDateRange.endDate) {
        setRange(customDateRange);
        localStorage.setItem("range", JSON.stringify(customDateRange));
      }
    }
  }, [months, dateMode, customDateRange]);

  // Fetch all categories for the filter
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const contextParams = getContextParams();
        const categories = await getAllCategoriesWithGroups(contextParams);
        setAllCategories(categories);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategories();
  }, [getContextParams]);

  // Fetch stats with filtered categories
  useEffect(() => {
    const fetchData = async () => {
      if (
        !range.startDate ||
        !range.endDate ||
        filteredCategoryIds.length === 0
      ) {
        return;
      }

      try {
        const contextParams = getContextParams();

        // Pass filtered category IDs to the API calls
        const params = {
          ...contextParams,
          categoryIds: filteredCategoryIds,
        };

        const [
          totals,
          allCategoryStats,
          expByCat,
          invBySource,
          expByVendor,
          upcomingPay,
          topExp,
          incbycateg,
        ] = await Promise.all([
          getTotals(range, params),
          getAllCategoryStats(range, params),
          getExpensesByCategory(range, params),
          getInvestmentsBySource(range, params),
          getExpensesByVendor(range, params),
          getUpcomingRecurringPayments(upcomingPaymentsPeriod, params),
          getTopExpenses(range, params),
          getIncomeByCategory(range, params),
        ]);

        setTotalStats(totals);
        setCategoryStats(allCategoryStats);
        setExpensesByCategory(expByCat);
        setInvestmentsBySource(invBySource);
        setExpensesByVendor(expByVendor);
        setUpcomingPayments(upcomingPay);
        setTopExpenses(topExp);
        setIncomeByCategory(incbycateg);
      } catch (error) {
        console.error("Error fetching stats:", error);
      }
    };

    fetchData();
  }, [range, upcomingPaymentsPeriod, getContextParams, filteredCategoryIds]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleUpcomingPaymentsPeriodChange = (newPeriod) => {
    setUpcomingPaymentsPeriod(newPeriod);
  };

  const getEffectiveMonths = () => {
    if (
      dateMode === "custom" &&
      customDateRange.startDate &&
      customDateRange.endDate
    ) {
      const start = new Date(customDateRange.startDate);
      const end = new Date(customDateRange.endDate);
      const monthDiff =
        (end.getFullYear() - start.getFullYear()) * 12 +
        (end.getMonth() - start.getMonth());
      return Math.max(1, monthDiff);
    }
    return months;
  };

  const getCurrentDateRange = () => {
    const format = (d) =>
      d ? new Date(d).toLocaleDateString("en-GB").replace(/\//g, "-") : "";
    return `${format(range.startDate)} to ${format(range.endDate)}`;
  };

  if (!totalStats || !categoryStats) {
    return (
      <Container component="main" maxWidth="sm">
        <Box sx={{ minHeight: "100vh", justifyContent: "center" }}>
          <CircularProgress size={50} sx={{ mr: 1, mt: 10 }} />
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", mt: 4, px: 2 }}>
      <Typography variant="h5" sx={{ mb: 2, textAlign: "center" }}>
        Financial Analytics Dashboard
      </Typography>

      {/* Date Controls */}
      <EnhancedDatePicker
        dateMode={dateMode}
        setDateMode={setDateMode}
        months={months}
        setMonths={setMonths}
        customDateRange={customDateRange}
        setCustomDateRange={setCustomDateRange}
        range={range}
        setRange={setRange}
        sx={{ mb: 3 }}
      />

      {/* Category Filter */}
      <CategoryFilter
        categories={allCategories}
        filteredCategoryIds={filteredCategoryIds}
        onFilterChange={setFilteredCategoryIds}
      />

      {/* Show current date range */}
      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
        Date Range: {getCurrentDateRange()}
      </Typography>

      {/* Tab Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="stats tabs"
        >
          <Tab label="Overview" />
          <Tab label="Analytics" />
          <Tab label="Recurring Payments" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Box>
          {/* Original Stats Content */}
          <Paper sx={{ p: 2, mb: 1, maxWidth: "1000px", mx: "auto" }}>
            <TotalStats {...totalStats} />
          </Paper>

          <Paper sx={{ p: 2, mb: 3 }}>
            <CategoryChartsPage
              text={
                dateMode === "custom" &&
                customDateRange.startDate &&
                customDateRange.endDate
                  ? getCurrentDateRange()
                  : months + " months"
              }
              data={categoryStats}
              months={getEffectiveMonths()}
              userCurrency={userCurrency}
            />
          </Paper>

          <Box display="flex" flexWrap="wrap" justifyContent="center" gap={2}>
            <Paper sx={{ p: 2, flex: "1 1 500px", maxWidth: "500px" }}>
              <RadialChart
                title="Expenses by Category"
                data={expensesByCategory}
                showLegend={false}
                userCurrency={userCurrency}
              />
            </Paper>
            <Paper sx={{ p: 2, flex: "1 1 500px", maxWidth: "500px" }}>
              <RadialChart
                title="Expenses by Vendor"
                data={expensesByVendor}
                showLegend={false}
                userCurrency={userCurrency}
              />
            </Paper>
            <Paper sx={{ p: 2, flex: "1 1 500px", maxWidth: "500px" }}>
              <RadialChart
                title="Income by Category"
                data={incomeByCategory}
                showLegend={false}
                userCurrency={userCurrency}
              />
            </Paper>
            <Paper sx={{ p: 2, flex: "1 1 500px", maxWidth: "500px" }}>
              <RadialChart
                title="Investments by Source"
                data={investmentsBySource}
                showLegend={false}
                userCurrency={userCurrency}
              />
            </Paper>
          </Box>

          <Paper sx={{ p: 2, mt: 3, maxWidth: "700px", mx: "auto" }}>
            <CategoryStatsChart
              text={
                dateMode === "custom" &&
                customDateRange.startDate &&
                customDateRange.endDate
                  ? getCurrentDateRange()
                  : months + " months"
              }
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              customDateRange={range}
              months={getEffectiveMonths()}
              userCurrency={userCurrency}
            />
          </Paper>
        </Box>
      )}

      {activeTab === 1 && (
        <Box>
          {/* New Analytics Content */}
          <Box
            display="grid"
            gridTemplateColumns="repeat(auto-fit, minmax(400px, 1fr))"
            gap={3}
          >
            {/* Top Expenses and Spending Velocity */}
            <Paper sx={{ p: 2 }}>
              <TopExpensesChart data={topExpenses} />
            </Paper>
          </Box>
        </Box>
      )}

      {activeTab === 2 && (
        <Box>
          {/* Recurring Payments Content */}
          <Box
            display="grid"
            gridTemplateColumns="repeat(auto-fit, minmax(400px, 1fr))"
            gap={3}
          >
            <Paper sx={{ p: 2 }}>
              <UpcomingPaymentsChart
                data={upcomingPayments}
                onPeriodChange={handleUpcomingPaymentsPeriodChange}
              />
            </Paper>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default StatsPage;
